import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useAdminLeaderboard, useAdminPhotos } from '../../hooks/useQuestAdmin';
import { currentQuarter, downloadCsv } from './questShared';
import {
  Avatar, BTN_SECONDARY, Card, DEPARTMENT_SOON, FILTER, Loading, PeriodSelect,
} from './adminUi';

// The full board — everyone with points, not the employees' top ten. A cluster
// filter narrows the list but keeps the company-wide positions. The brief's
// department filter is here but off until there is a department to filter by.
function LeaderboardTab() {
  const [period, setPeriod] = useState(currentQuarter());
  const [cluster, setCluster] = useState('');
  const q = useAdminLeaderboard(period, cluster);
  const rows = q.data?.rows || [];
  // photos for the first hundred — past that, nobody is looking at faces
  const photos = useAdminPhotos(rows.slice(0, 100).map((r) => r.entra_user_id));

  const exportCsv = () => downloadCsv(`nano-quest-leaderboard-${period}${cluster ? `-${cluster}` : ''}.csv`, [
    ['Position', (r) => r.position],
    ['Name', (r) => r.display_name],
    ['Job title', (r) => r.job_title],
    ['Department', (r) => r.department],
    ['Region', (r) => r.region],
    ['Cluster', (r) => r.cluster],
    ['Points', (r) => r.points],
  ], rows);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodSelect value={period} onChange={setPeriod} />
        <select value={cluster} onChange={(e) => setCluster(e.target.value)} className={FILTER} aria-label="Cluster">
          <option value="">Every cluster</option>
          {(q.data?.clusters || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select disabled title={DEPARTMENT_SOON} className={`${FILTER} cursor-not-allowed bg-gray-50 text-gray-400`}>
          <option>Every department (coming soon)</option>
        </select>
        <button type="button" onClick={exportCsv} disabled={!rows.length} className={`${BTN_SECONDARY} ml-auto`}>
          <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <Card>
        <Loading q={q} what="the leaderboard" />
        {q.data && !rows.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">Nobody has points in this period.</p> : null}
        {rows.length ? (
          <table className="min-w-full text-sm">
            <thead className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 w-16">#</th>
                <th className="px-4 py-2">Name</th>
                <th className="hidden px-4 py-2 md:table-cell" title={DEPARTMENT_SOON}>Department</th>
                <th className="hidden px-4 py-2 sm:table-cell">Region</th>
                <th className="px-4 py-2 text-right">Points</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.entra_user_id}>
                  <td className="px-4 py-2 font-bold tabular-nums text-gray-700">{r.position}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      <Avatar name={r.display_name} photo={photos[r.entra_user_id]} />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900">{r.display_name || 'Unknown'}</div>
                        {r.job_title ? <div className="text-xs text-gray-400">{r.job_title}</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-4 py-2 text-gray-400 md:table-cell" title={r.department ? undefined : DEPARTMENT_SOON}>
                    {r.department || '—'}
                  </td>
                  <td className="hidden px-4 py-2 text-gray-600 sm:table-cell">
                    {[r.region, r.cluster].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-4 py-2 text-right font-bold tabular-nums">{r.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Card>
    </div>
  );
}

export default LeaderboardTab;
